<script setup lang="ts">
import { computed, markRaw } from 'vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import NotificationBadge from '@/InventoryControlModule/components/NotificationBadge.vue';
import InventoryAlertCell from '@/InventoryControlModule/components/InventoryTable/InventoryAlertCell.vue';
import RecommendTag from '@/corePagesModule/components/RecommendTag.vue';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import type { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import type { TableType } from '@/InventoryControlModule/composables/useTableState';
import type { IMonthlyBusinessData } from '@/InventoryControlModule/domains/entities/MonthlyData';
import type { InventoryTableViewModel } from '@/InventoryControlModule/pages/inventoryTableViewModel';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import { IEditTransactionValue } from '@/InventoryControlModule/pages/useInventoryStore/type';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * InventoryMonthlyColumn コンポーネントのプロパティ
 */
interface Props {
  /** 対象の月 (相対月) */
  month: number;
  /** ビジネスアイテム */
  item: IParentBusiness;
  /** 仮想化アイテムタイプ */
  virtualItemType: 'parent' | 'child';
  /** 月次データ */
  monthlyData: Record<string, IMonthlyBusinessData>;
  /** 表示対象のヘッダーリスト */
  headerList: string[];
  /** 開いているビジネスID */
  isOpeningBusinessId: string;
  /** インベントリViewModel */
  inventoryVm: InventoryTableViewModel;
  /** 月次エンドヘッダー */
  monthlyEndHeader: string;
  /** ハイライトされているか */
  isHighlighted: boolean;
  /** 数値編集モードかどうか */
  isEditTransactionMode: boolean;
  /** 当該月の数値編集モードの値 */
  currentMonthEditTransactionValue?: IEditTransactionValue[];
  /** テーブル数値スタイル関数 */
  tableNumberStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  /** 在庫ステータススタイル関数 */
  getNowInventoryStatusClass: (business: IParentBusiness, tableType?: TableType) => string;
  /** 実行日フォーマット関数 */
  getExecutionDateFormatted: (business: IParentBusiness) => string;
  /** 負の値判定関数 */
  isNegative: (value?: number) => boolean;
  /** 近接推奨チェック関数 */
  checkNearestRecommend: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => boolean;
  /** 到着ステータス取得関数 */
  getArrivalStatusWhenDisplayButton: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    businessId: string,
    month: number
  ) => ArrivalStatus;
}

const props = defineProps<Props>();

const emits = defineEmits<{ (e: 'updateEditTransactionValue', value: IEditTransactionValue) }>();

/**
 * 最適化されたテーブル計算Composable
 * WeakMapキャッシュによる月次データ計算の高速化
 */
const { getMonthlyData } = useTableCalculation();

/**
 * キャッシュされた月次データ
 */
const cachedMonthlyData = computed(() => {
  return getMonthlyData(props.monthlyData, props.item.businessId, props.month);
});

// Recommendedが含まれるかどうか
const hasRecommended = computed(() => {
  const list = cachedMonthlyData.value?.arrivalStatusList;
  return Array.isArray(list) && list.includes(ArrivalStatus.Recommended);
});

/**
 * 月次トランザクションキーのリスト
 * @description 対象月の全トランザクションキーを取得
 */
const monthlyTransactionKeys = computed(() => {
  const keys = props.inventoryVm.getMonthlyTransactionHeaderKeys(props.month, false);
  // 表示対象のキーのみをフィルタリングして返す
  return keys.filter((key) => props.headerList.includes(`transaction-${props.month}-${key}`));
});

/**
 * スタイルクラスの計算
 */
const tableNumberStyle = computed(() => {
  return props.tableNumberStyleClass(props.item, props.isOpeningBusinessId, 'virtualized');
});

const inventoryStatusStyle = computed(() => {
  return props.getNowInventoryStatusClass(props.item, 'virtualized');
});

/**
 * 組み合わせスタイルクラス
 */
const combinedStyles = computed(() => {
  return [tableNumberStyle.value, inventoryStatusStyle.value];
});

/**
 * 親タイプの判定
 */
const isParent = computed(() => props.virtualItemType === 'parent');

/**
 * 実行日フォーマット済み文字列
 */
const formattedExecutionDate = computed(() => {
  return isParent.value ? props.getExecutionDateFormatted(props.item) : '';
});

/**
 * 推奨チェック結果
 */
const nearestRecommendCheck = computed(() => {
  return isParent.value ? props.checkNearestRecommend(props.monthlyData, props.item.businessId, props.month) : false;
});

/**
 * 到着ステータ
 */
const arrivalStatus = computed(() => {
  return isParent.value ? props.getArrivalStatusWhenDisplayButton(props.monthlyData, props.item.businessId, props.month) : null;
});

const isEditTransactionInput = computed(() => {
  if (!props.isEditTransactionMode) return false;
  return {
    arrival: props.virtualItemType === 'parent' && props.month < 0,
    endInventory: props.virtualItemType === 'parent' && props.month < 0,
    announce: props.virtualItemType === 'child',
    supplierAnnounce: props.virtualItemType === 'parent' && props.month >= 0,
    salesResult: props.virtualItemType === 'child' && props.month < 0,
    nowInventory: props.virtualItemType === 'parent' && props.month === 0,
  };
});

/**
 * 数値編集inputのmodelValueを取得
 * @param transactionKey - 数値列キー
 */
const getEditTransactionInputModelValue = (transactionKey: string): number => {
  // supplierAnnounceはAPIのtransactionTypeがsupplierForecast
  // nowInventoryはAPIのtransactionTypeがcurrentInventory
  const searchType =
    transactionKey === 'supplierAnnounce' ? 'supplierForecast' : transactionKey === 'nowInventory' ? 'currentInventory' : transactionKey;
  const foundValue = props.currentMonthEditTransactionValue?.find(
    (value) => value.type === searchType && (props.virtualItemType === 'child' ? value.baseBusinessId === props.item.businessId : true)
  );
  return foundValue?.value ?? cachedMonthlyData.value[transactionKey] ?? 0;
};

/**
 * 数値編集のinputイベントハンドラ
 * @param val - 変更後の値
 * @param transactionKey - 数値列キー
 */
const inputEditTransactionHandler = (val: string, transactionKey: string) => {
  const originValue = cachedMonthlyData.value[transactionKey] ?? 0;

  const editTransactionValueObj = {
    // supplierAnnounceはAPIのtransactionTypeがsupplierForecast
    // nowInventoryはAPIのtransactionTypeがcurrentInventory
    type:
      transactionKey === 'supplierAnnounce' ? 'supplierForecast' : transactionKey === 'nowInventory' ? 'currentInventory' : transactionKey,
    month: props.month,
    value: parseFloat(val),
    originValue: originValue,
  };
  if (props.virtualItemType === 'child') {
    editTransactionValueObj['baseBusinessId'] = props.item.businessId;
    if (transactionKey === 'announce') {
      const receivedYearMonth = props.item.customerAnnounce?.find((v) => {
        const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month);
        return v.year === targetYearMonth.year && v.month === targetYearMonth.month;
      })?.manageInitiatedYearMonth;
      if (receivedYearMonth) {
        editTransactionValueObj['receivedYearMonth'] = receivedYearMonth;
      } else if (props.month > 0) {
        // 新規作成かつ編集対象年月が翌月以降の場合、操作日の前月を受領月とする
        editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);
      } else {
        // 新規作成かつ編集対象年月が当月以前の場合、編集対象年月の前月を受領月とする
        editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month - 1);
      }
    }
  } else if (transactionKey === 'supplierAnnounce') {
    // 親取引の仕入先内示は作成年月を送る（フィールドは受領年月と共用）
    const createdYearMonth = (props.item as any).supplierAnnounce?.find((v: any) => {
      const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month);
      return v.year === targetYearMonth.year && v.month === targetYearMonth.month;
    })?.manageInitiatedYearMonth;
    if (createdYearMonth) {
      editTransactionValueObj['receivedYearMonth'] = createdYearMonth;
    } else if (props.month > 0) {
      // 新規作成かつ編集対象年月が翌月以降の場合、操作日の前月を受領月とする
      editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);
    } else {
      // 新規作成かつ編集対象年月が当月以前の場合、編集対象年月の前月を作成月とする(仕入先内示で該当するのは当月のみの想定)
      editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month - 1);
    }
  } else if (transactionKey === 'nowInventory') {
    // 現在庫の場合はexecutionDateの年月を送る
    if (props.item.executionDate) {
      editTransactionValueObj['executionDate'] = props.item.executionDate;
    }
  }
  emits('updateEditTransactionValue', editTransactionValueObj);
  return;
};

/**
 * 子取引の情報を変更した際に変わる集計の値
 */
const getChangedAggregateBusinessTransactionValue = computed(() => {
  return {
    salesResult:
      cachedMonthlyData.value.salesResult +
      (props.currentMonthEditTransactionValue?.reduce((acc: number, val: IEditTransactionValue) => {
        return val.type === 'salesResult' ? acc + val.value - val.originValue : acc;
      }, 0) ?? 0),
    announce:
      cachedMonthlyData.value.announce +
      (props.currentMonthEditTransactionValue?.reduce((acc: number, val: IEditTransactionValue) => {
        return val.type === 'announce' ? acc + val.value - val.originValue : acc;
      }, 0) ?? 0),
  };
});
</script>

<template>
  <!-- 月次データ列 (最適化版) -->
  <template v-for="transactionKey in monthlyTransactionKeys" :key="transactionKey">
    <!-- 現在庫 -->
    <!-- 数値編集モード時の現在庫 -->
    <EzCell
      v-if="transactionKey === 'nowInventory' && isEditTransactionInput['nowInventory']"
      is-number
      is-highlighted
      :class="combinedStyles"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      <div class="now-inventory-edit-container">
        <NumberInputWithComma
          class="table-input now-inventory-input"
          :model-value="getEditTransactionInputModelValue('nowInventory')"
          @input="(e) => inputEditTransactionHandler(e, 'nowInventory')"
        />
        <span class="execution-date-label">({{ formattedExecutionDate }})</span>
      </div>
    </EzCell>
    <!-- 通常表示時の現在庫 -->
    <EzCell
      v-else-if="transactionKey === 'nowInventory'"
      :is-number="true"
      :is-negative="isNegative(cachedMonthlyData.nowInventory)"
      :is-highlighted="isHighlighted"
      :class="combinedStyles"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      {{ isParent ? `${item.inventory?.roundToDecimal(2).addComma() ?? 0} (${formattedExecutionDate})` : '--' }}
    </EzCell>

    <!-- 予入荷 -->
    <EzCell
      v-else-if="transactionKey === 'expectedArrival'"
      :is-number="true"
      :is-negative="isNegative(cachedMonthlyData.exceptedArrivalForNormalMode)"
      :is-highlighted="isHighlighted"
      :class="tableNumberStyle"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      <template v-if="!isParent"> -- </template>
      <template v-else>
        <div class="inventory-badge__container" :class="tableNumberStyle">
          <!-- ArrivalStatusがRecommendedのときは、推奨ボタンとBadgeのみ表示し、数値は表示しない -->
          <template v-if="hasRecommended">
            <router-link :to="{ name: 'recommend-order', params: { id: item.businessId } }" target="_blank">
              <RecommendTag :status="ArrivalStatus.Recommended" />
            </router-link>
            <div class="inventory-badge__list">
              <template v-for="arrivalStatusItem in cachedMonthlyData.arrivalStatusList" :key="`${item.businessId}-${arrivalStatusItem}`">
                <NotificationBadge
                  v-if="arrivalStatusItem !== ArrivalStatus.Recommended && arrivalStatusItem !== ArrivalStatus.Nothing"
                  class="inventory-badge"
                  :style="inventoryVm.notificationBadgeStyle(arrivalStatusItem, false)"
                  :arrival-status="arrivalStatusItem"
                  :unit="item.unit"
                  :un-paid-arrivals="cachedMonthlyData.unPaidArrival"
                  :approval-requested-arrival="cachedMonthlyData.approvalRequestedArrival"
                />
              </template>
            </div>
          </template>
          <template v-else>
            <div v-if="(cachedMonthlyData.exceptedArrivalForNormalMode ?? 0) > 0" class="inventory-badge__list">
              <template v-for="arrivalStatusItem in cachedMonthlyData.arrivalStatusList" :key="`${item.businessId}-${arrivalStatusItem}`">
                <NotificationBadge
                  v-if="arrivalStatusItem !== ArrivalStatus.Nothing"
                  class="inventory-badge"
                  :style="inventoryVm.notificationBadgeStyle(arrivalStatusItem, false)"
                  :arrival-status="arrivalStatusItem"
                  :unit="item.unit"
                  :un-paid-arrivals="cachedMonthlyData.unPaidArrival"
                  :approval-requested-arrival="cachedMonthlyData.approvalRequestedArrival"
                />
              </template>
            </div>
            {{ cachedMonthlyData.exceptedArrivalForNormalMode?.roundToDecimal(2).addComma() }}
          </template>
        </div>
      </template>
    </EzCell>

    <!-- 予末在庫 -->
    <EzCell
      v-else-if="transactionKey === 'expectedEndInventory'"
      :is-number="true"
      :is-negative="isParent && isNegative(cachedMonthlyData.exceptedEndInventory)"
      :is-highlighted="isHighlighted"
      :class="tableNumberStyle"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      {{ isParent ? cachedMonthlyData.exceptedEndInventory?.roundToDecimal(2).addComma() : '--' }}
    </EzCell>

    <!-- アラート -->
    <EzCell
      v-else-if="transactionKey === 'alert'"
      :is-number="true"
      :is-negative="isNegative(cachedMonthlyData[transactionKey])"
      :is-highlighted="isHighlighted"
      :class="tableNumberStyle"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      <InventoryAlertCell :prop-business="item" :prop-relative-month="month" />
    </EzCell>

    <!-- 数値編集モード -->
    <EzCell
      v-else-if="isEditTransactionInput[transactionKey]"
      is-number
      is-highlighted
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      <NumberInputWithComma
        class="table-input"
        :model-value="getEditTransactionInputModelValue(transactionKey)"
        @input="(e) => inputEditTransactionHandler(e, transactionKey)"
      />
    </EzCell>

    <!-- 数値編集モード時の実績・内示 -->
    <EzCell
      v-else-if="
        isEditTransactionMode &&
        virtualItemType === 'parent' &&
        (transactionKey === 'announce' || (month < 0 && transactionKey === 'salesResult')) &&
        getChangedAggregateBusinessTransactionValue[transactionKey] !== cachedMonthlyData[transactionKey]
      "
      is-number
      :is-negative="isParent && isNegative(cachedMonthlyData[transactionKey])"
      :is-highlighted="isHighlighted"
      :class="tableNumberStyle"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      {{
        `${cachedMonthlyData[transactionKey]?.roundToDecimal(2).addComma() ?? 0} → ${
          getChangedAggregateBusinessTransactionValue[transactionKey].roundToDecimal(2).addComma() ?? 0
        } `
      }}
    </EzCell>

    <!-- それ以外 -->
    <EzCell
      v-else
      is-number
      :is-negative="isParent && isNegative(cachedMonthlyData[transactionKey])"
      :is-highlighted="isHighlighted"
      :class="tableNumberStyle"
      :has-right-border="transactionKey === monthlyEndHeader"
    >
      {{
        isParent || transactionKey === 'announce' || transactionKey === 'salesResult'
          ? cachedMonthlyData[transactionKey]?.roundToDecimal(2).addComma() ?? 0
          : '--'
      }}
    </EzCell>
  </template>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';
</style>
